import React from 'react';
import { Controller } from 'react-hook-form';

import styled from '@emotion/styled';
import { css } from '@emotion/core';

import { ContextCardForm } from '~/api/models/leadads/context-card';

import { refreshingSubtle } from '~/theseus/styles/keyframes';
import SectionToggle from '~/theseus/components/SectionToggle';
import Button from '~/theseus/components/Button';
import InputWithLengthWarningsInner from '~/theseus/components/Form/InputWithLengthWarnings';
import InputInner from '~/theseus/components/Form/Input';
import withError from '~/theseus/components/Form/withError';

import Field from '../../Field';

import useContextCardFormState from './useContextCardForm';
import ImageSelector from './ImageSelector';

const FormContainer = styled.div`
  padding: 20px;
`;

const Input = withError(InputInner);
const InputWithLengthWarnings = styled(withError(InputWithLengthWarningsInner))`
  flex-grow: 2;
`;

const loadingAnimation = ({ isLoading }) => {
  if (isLoading) {
    return css`
      animation: ${refreshingSubtle} 1s linear infinite;
      pointer-events: none;
    `;
  }

  return css``;
};

const Form = styled.form`
  ${loadingAnimation}
`;

const InvisibleLabel = ({ children }) => <span className="label" css={{ opacity: 0 }}>{ children }</span>;

interface Props {
  propertyImages: string[];
  showBackButton: boolean;
  onCancel: () => void;
  isCreatingCard: boolean;
  onCreate: (form: ContextCardForm) => void;
}

function ContextCardCreator(props: Props) {
  const {
    propertyImages,
    showBackButton,
    onCancel,
    isCreatingCard,
    onCreate,
  } = props;

  const form = useContextCardFormState();

  return (
    <FormContainer>
      {
        showBackButton
          && (
            <Button
              variant="plain"
              onClick={onCancel}
              disabled={isCreatingCard}
              size="md"
              aria-label="Cancel welcome message creation"
            >
              Cancel
            </Button>
          )
      }
      <Form aria-busy={isCreatingCard} isLoading={isCreatingCard} onSubmit={form.handleSubmit(onCreate)} data-testid="context-card-form">
        <Field htmlFor="headline">
          <span className="label">Headline</span>
          <InputWithLengthWarnings
            type="text"
            id="headline"
            name="headline"
            ref={form.register({ required: 'Headline is required' })}
            maxLength={60}
            placeholder="Briefly describe what people will receive. (Required)"
            error={form.errors.headline}
            disabled={isCreatingCard}
          />
        </Field>
        {
          propertyImages?.length > 0
            && (
            <Controller
              as={ImageSelector}
              name="image"
              control={form.control}
              images={propertyImages}
            />
            )

        }
        <Field htmlFor="form-layout">
          <span className="label">Layout</span>
          <select id="form-layout" name="layout" ref={form.register} defaultValue="LIST_STYLE">
            <option value="LIST_STYLE">List</option>
            <option value="PARAGRAPH_STYLE">Paragraph</option>
          </select>
        </Field>

        <SectionToggle enabledOn={form.layout === 'LIST_STYLE'}>
          <Field htmlFor="content-1">
            <span className="label">Content</span>
            <InputWithLengthWarnings
              id="content-1"
              name="list[0]"
              ref={form.register({ validate: form.validateList })}
              disabled={isCreatingCard}
              error={form.errors.list && form.errors.list[0]}
            />
          </Field>
          <Field htmlFor="content-2">
            <InvisibleLabel>Content</InvisibleLabel>
            <InputWithLengthWarnings
              id="content-2"
              name="list[1]"
              ref={form.register}
              disabled={isCreatingCard}
            />
          </Field>
          <Field htmlFor="content-3">
            <InvisibleLabel>Content</InvisibleLabel>
            <InputWithLengthWarnings
              id="content-3"
              name="list[2]"
              ref={form.register}
              disabled={isCreatingCard}
            />
          </Field>
          <Field htmlFor="content-4">
            <InvisibleLabel>Content</InvisibleLabel>
            <InputWithLengthWarnings
              id="content-4"
              name="list[3]"
              ref={form.register}
              disabled={isCreatingCard}
            />
          </Field>
          <Field htmlFor="content-5">
            <InvisibleLabel>Content</InvisibleLabel>
            <InputWithLengthWarnings
              id="content-5"
              name="list[4]"
              ref={form.register}
              disabled={isCreatingCard}
            />
          </Field>
        </SectionToggle>

        <SectionToggle disabledOn={form.layout === 'LIST_STYLE'}>
          <Field htmlFor="content">
            <span className="label">Content</span>
            <Input
              as="textarea"
              name="paragraph"
              id="content"
              ref={form.register({ required: 'Content is required' })}
              placeholder="Enter text that tells people about your product or service. (Required)"
              disabled={isCreatingCard}
              error={form.errors.paragraph}
            />
          </Field>
        </SectionToggle>

        <Button type="submit" size="md" disabled={isCreatingCard}>
          Save and use Welcome Message
        </Button>
      </Form>
    </FormContainer>
  );
}

export default ContextCardCreator;
